/* ============================================================================
 * Raisy /pitchdeck — THE STORY  ·  v2026.09.3 (D-061…D-063)  ·  "v4"
 *
 * v3's vision, re-opened so the deck OWNS the story. His 5-founders hook and the
 * "era of the one-person company" slide are GONE from this version (they stay in
 * v1–v3 — that framing is smaller than the story this deck tells).
 *
 *   THE HAMMER     One day a robot will attend a pitch event — and it is DRAWN:
 *                  a row of seated attendees with one machine among them, the
 *                  term sheet at its side.
 *   THE LENS       Every entity = input · process · output (arrives BEFORE the
 *                  problems — it is the lens the whole deck looks through).
 *   PROBLEM 1      Two companies can only talk through people.
 *   PROBLEM 2      Agents can think. They cannot take the handoff.
 *   THE NETWORK    Output signatures matched to input signatures, drawn as a
 *                  network (the Yellow Pages that can negotiate).
 *   THE ENGINE     the All-Rounder Formula, the kill list, the dials, the harness.
 *
 * Every visual is drawn inline (SVG / deck idioms) — no rasters, so nothing can
 * crop and every number stays crisp at any scale.
 * Sources: playano-docs/raisy/economy_harness/ + RAISY_PITCH_P3_DATA_FOR_PLOTS.md.
 * ========================================================================== */
import { motion } from 'framer-motion';
import { Fragment, ReactElement } from 'react';
import { cn } from '@/lib/utils';
import { CountUp, Kicker, Shell, Stat, fadeUp, stagger } from './slides';
import { DECK_SLIDES_VISION, PRELOAD_VISION } from './slidesVision';

const EASEX = [0.22, 1, 0.36, 1] as const;
const TEAL = '#4dd4e0';

/* ========================================================================
 * THE HAMMER · the scene — a row of attendees, one of them a machine
 * ====================================================================== */
function PitchRoom() {
  return (
    <svg
      className="pd-room"
      viewBox="0 0 1180 310"
      role="img"
      aria-label="A row of seated attendees; the last one is a machine, with a signed sheet beside it"
    >
      <defs>
        <radialGradient id="pdSpot" cx="50%" cy="0%" r="75%">
          <stop offset="0%" stopColor="rgba(10,179,191,0.42)" />
          <stop offset="100%" stopColor="rgba(10,179,191,0)" />
        </radialGradient>
      </defs>
      {/* the spotlight finds the machine, not the room */}
      <ellipse cx="880" cy="150" rx="250" ry="160" fill="url(#pdSpot)" />
      <line x1="30" y1="268" x2="1150" y2="268" stroke="rgba(255,255,255,0.13)" strokeWidth="2" />

      {/* five people, seated */}
      {[0, 1, 2, 3, 4].map((i) => (
        <g key={i} transform={`translate(${130 + i * 150},0)`} opacity="0.5">
          <circle cx="0" cy="150" r="29" fill="none" stroke="#eaf6f7" strokeWidth="2.5" />
          <path d="M-38 268 v-42 a38 38 0 0 1 76 0 v42" fill="none" stroke="#eaf6f7" strokeWidth="2.5" />
        </g>
      ))}

      {/* the machine — same row, one seat further along */}
      <g transform="translate(880,0)">
        <line x1="30" y1="120" x2="30" y2="84" stroke={TEAL} strokeWidth="2.5" />
        <circle cx="30" cy="78" r="5.5" fill={TEAL} />
        <rect x="2" y="120" width="56" height="56" rx="9" fill="rgba(10,179,191,0.12)" stroke={TEAL} strokeWidth="3" />
        <circle cx="20" cy="148" r="4.5" fill={TEAL} />
        <circle cx="40" cy="148" r="4.5" fill={TEAL} />
        <path d="M-8 268 v-42 a38 38 0 0 1 76 0 v42" fill="none" stroke={TEAL} strokeWidth="3" />
      </g>

      {/* what it came for */}
      <motion.path
        d="M 936 168 C 976 140 1000 138 1018 150"
        fill="none"
        stroke={TEAL}
        strokeWidth="2.5"
        strokeDasharray="7 7"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.3, delay: 1.7 }}
      />
      <g transform="translate(1026,124) rotate(-7)">
        <rect x="0" y="0" width="64" height="82" rx="5" fill="rgba(255,255,255,0.07)" stroke="rgba(77,212,224,0.65)" strokeWidth="2" />
        <line x1="12" y1="26" x2="52" y2="26" stroke="rgba(234,246,247,0.65)" strokeWidth="2.5" />
        <line x1="12" y1="43" x2="52" y2="43" stroke="rgba(234,246,247,0.4)" strokeWidth="2.5" />
        <line x1="12" y1="60" x2="38" y2="60" stroke="rgba(234,246,247,0.4)" strokeWidth="2.5" />
      </g>

      <text className="pd-room-t" x="430" y="298" textAnchor="middle">
        the room
      </text>
      <text className="pd-room-t" x="900" y="298" textAnchor="middle" fill={TEAL}>
        the counterparty
      </text>
    </svg>
  );
}

export function SHammer({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Look up.</Kicker>
        <motion.h1 className="pd-h1 pd-hammer-v" variants={fadeUp}>
          One day, a robot will attend a pitch event.
          <small>Not as a guest — as the counterparty.</small>
        </motion.h1>
        <motion.div variants={fadeUp}>
          <PitchRoom />
        </motion.div>
        <motion.p className="pd-fine" variants={fadeUp}>
          Everything in this deck is what has to be true first.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * THE LENS · every entity = input › process › output
 * ====================================================================== */
const TRIO = [
  { t: 'Input', h: 'what it needs', p: 'capital · a tool · a licence · a partner · a person · data', hot: false },
  { t: 'Process', h: 'what it does', p: 'the work that turns those needs into something the market wants', hot: false },
  { t: 'Output', h: 'what it offers', p: 'a product · a service · capacity · expertise · capital', hot: true },
];

export function SEntityShape({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Finding the shape of everything…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Every entity on the network is the same three things.
          <small>An input, a process, an output.</small>
        </motion.h1>
        <motion.div className="pd-trio" variants={stagger}>
          {TRIO.map((x, i) => (
            <Fragment key={x.t}>
              {i > 0 && (
                <motion.div className="pd-trio-a" variants={fadeUp}>
                  ›
                </motion.div>
              )}
              <motion.div className={cn('pd-trio-c', x.hot && 'hot')} variants={fadeUp}>
                <span className="t">{x.t}</span>
                <h4>{x.h}</h4>
                <p>{x.p}</p>
              </motion.div>
            </Fragment>
          ))}
        </motion.div>
        <motion.p className="pd-vsay" variants={fadeUp}>
          A company, a fund, a university, an agency — <b>and now an agent</b>. Same three
          openings, whatever is behind them.
        </motion.p>
        <motion.p className="pd-fine" variants={fadeUp}>
          We call the first and the last a signature: an <b>input signature</b> and an{' '}
          <b>output signature</b>. That is the entire data model a network of companies needs.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PROBLEM 1 · the deal funnel — human-mediated, therefore slow
 * ====================================================================== */
const SLOW = [
  { num: '01', title: 'Everyone you can reach', desc: 'the long list' },
  { num: '02', title: 'First meeting', desc: 'timing mismatch kills most' },
  { num: '03', title: 'Deep evaluation', desc: 'ghosted · no budget' },
  { num: '04', title: 'Diligence', desc: 'legal & tech friction' },
  { num: '05', title: 'Signed', desc: 'the 1–2% that survive', hero: true },
];

export function SProblemSlow({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The problem…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Two companies can only talk through people.
          <small>And people are slow.</small>
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          Every email, call, follow-up and handover is a person translating between two
          organisations. It works — which is exactly why nobody questions what it costs.
        </motion.p>
        <motion.div className="pd-journey" variants={fadeUp}>
          <div className="pd-jline">
            <motion.i
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 2.2, ease: EASEX, delay: 0.8 }}
              style={{ transformOrigin: 'left' }}
            />
          </div>
          <div className="pd-jrow">
            {SLOW.map((m, i) => (
              <motion.div
                key={m.title}
                className={cn('pd-jnode', m.hero && 'hero')}
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASEX, delay: 0.9 + i * 0.28 }}
              >
                <div className="pd-jdot" />
                <div className="pd-jnum">{m.num}</div>
                <div className="pd-jtitle">{m.title}</div>
                <div className="pd-jdesc">{m.desc}</div>
              </motion.div>
            ))}
          </div>
          <div className="pd-jpath">
            Every stage in between is <b>a human doing admin</b> — and every handover loses time.
          </div>
        </motion.div>
        <motion.div
          className="pd-stats"
          style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: 20 }}
          variants={stagger}
        >
          <Stat
            n={<CountUp to={57} duration={3.2} />}
            l="meetings to close a successful raise — a failed one stops at 14"
            s="Metal, 2026"
          />
          <Stat
            n={<CountUp to={80} duration={3.2} />}
            l="opportunities an investor reviews for one decision"
            s="Affinity, 2026"
          />
          <Stat
            n={<CountUp to={2} suffix="%" duration={3.2} />}
            l="of cold outreach ever becomes a term sheet"
            s="vcbeast, 2026 · directional"
          />
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PROBLEM 2 · the machine cannot take the handoff
 * ====================================================================== */
const MISSING = [
  { t: 'Reach a human', d: 'an endpoint that hires, briefs and pays a person — and knows which person' },
  { t: 'Believe the result', d: 'a human in the loop who checks the evidence before anything is committed' },
  { t: 'Commit on paper', d: 'the agreement, the signature, and the record behind both' },
];

export function SProblemMachine({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>And the other half is missing…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Agents can already think.
          <small>They cannot yet transact with humans.</small>
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          A model can write the perfect message. It cannot hire the photographer, check the
          photos that come back, pay the invoice, or get the signature. That is not an
          intelligence problem. It is infrastructure.
        </motion.p>
        <motion.div
          className="pd-vgrid"
          style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: 24 }}
          variants={stagger}
        >
          {MISSING.map((m) => (
            <motion.div key={m.t} className="pd-vcard" variants={fadeUp}>
              <h4>{m.t}</h4>
              <p className="pd-vbody">{m.d}</p>
            </motion.div>
          ))}
        </motion.div>
        <motion.p className="pd-vsay" variants={fadeUp}>
          We are building to be the <b>bridged specialist for human interaction</b> — the layer
          that lets a machine transact with people without asking anyone to trust it blindly.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * THE NETWORK · output signatures matched to input signatures.
 * Every entity's OUTPUT on the left, every entity's INPUT on the right; the
 * matches light up. Drawn, not described — this is the Yellow Pages claim.
 * ====================================================================== */
const NET = [
  { n: 'A · Factory', out: 'hardware — 12 product lines', inn: 'distribution in five markets' },
  { n: 'B · Distributor', out: 'distribution · 5 markets', inn: 'new hardware lines' },
  { n: 'C · Fund', out: 'capital · $1–5M cheques', inn: 'deal flow' },
  { n: 'D · School', out: 'graduates · 400 a year', inn: 'curriculum content' },
  { n: 'E · Publisher', out: 'a content library', inn: 'institutional adoption' },
  { n: 'F · Agency', out: 'creative capacity', inn: 'retainers' },
];
/** from-output → to-input. `on` = the matched pair we are highlighting. */
const EDGES = [
  { f: 0, t: 1, on: true },
  { f: 1, t: 0, on: true },
  { f: 2, t: 4, on: false },
  { f: 3, t: 5, on: false },
];

function SignatureNetwork() {
  const ROW = 62;
  const y = (i: number) => 28 + i * ROW; // chip centre line
  return (
    <svg
      className="pd-net"
      viewBox="0 0 1180 400"
      role="img"
      aria-label="Six entities each showing an output signature on the left and an input signature on the right, with matched pairs joined"
    >
      {/* column heads */}
      <text className="pd-net-n" x="24" y="14">
        output signature — what it offers
      </text>
      <text className="pd-net-n" x="1156" y="14" textAnchor="end">
        input signature — what it needs
      </text>

      {/* the wires (behind the chips) */}
      {EDGES.map((e) => {
        const yf = y(e.f);
        const yt = y(e.t);
        const d = `M 306 ${yf} C 520 ${yf} 660 ${yt} 874 ${yt}`;
        return e.on ? (
          <motion.path
            key={`${e.f}-${e.t}`}
            d={d}
            fill="none"
            stroke={TEAL}
            strokeWidth="2.5"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.6, delay: 1.2, ease: EASEX }}
          />
        ) : (
          <path
            key={`${e.f}-${e.t}`}
            d={d}
            fill="none"
            stroke="rgba(255,255,255,0.14)"
            strokeWidth="1.5"
            strokeDasharray="5 7"
          />
        );
      })}

      {/* the chips */}
      {NET.map((e, i) => {
        const lit = i === 0 || i === 1;
        return (
          <Fragment key={e.n}>
            <rect
              className={cn('pd-net-box', lit && 'on')}
              x="20"
              y={y(i) - 23}
              width="286"
              height="46"
              rx="10"
            />
            <text className={cn('pd-net-n', !lit && 'dim')} x="36" y={y(i) - 3}>
              {e.n}
            </text>
            <text className="pd-net-s" x="36" y={y(i) + 16}>
              {e.out}
            </text>

            <rect
              className={cn('pd-net-box', lit && 'on')}
              x="874"
              y={y(i) - 23}
              width="286"
              height="46"
              rx="10"
            />
            <text className={cn('pd-net-n', !lit && 'dim')} x="890" y={y(i) - 3}>
              {e.n}
            </text>
            <text className="pd-net-s" x="890" y={y(i) + 16}>
              {e.inn}
            </text>
          </Fragment>
        );
      })}

      {/* the crossing pair, named */}
      <text className="pd-net-cap" x="590" y="388" textAnchor="middle">
        A.out › B.in ✓ · B.out › A.in ✓ — a two-way fit, found by arithmetic
      </text>
    </svg>
  );
}

export function SNetwork({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Writing the standard…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          One company's output signature is another's input signature.
          <small>On a directory, the fit is found before anyone writes an email.</small>
        </motion.h1>
        <motion.div variants={fadeUp}>
          <SignatureNetwork />
        </motion.div>
        <motion.p className="pd-fine" variants={fadeUp}>
          Today that fit is discovered by talking to everyone. As signatures it is discovered by
          arithmetic — like the Yellow Pages, except <b>the directory can also negotiate</b>.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * THE FORMULA — the centrepiece
 * ====================================================================== */
const FORCES = [
  { s: 'Θ_ch', f: 'who trusts you', k: 'warm intro ×13' },
  { s: 'Θ_sig', f: 'what it cost you to be believed', k: 'cheap talk = 0' },
  { s: 'V(t)', f: 'how fast attention dies', k: '7× in 1h · 60× in 24h' },
  { s: 'Φ', f: 'how much a person can hold', k: '≤ 4 things' },
  { s: 'Ĉ', f: 'how much signal gets through', k: '50–125 words' },
  { s: 'E', f: 'how often they have seen you', k: 'peak ~10' },
  { s: 'F', f: 'whether you followed up', k: '5 → ×1.96 · 8 → ×0.81' },
];

export function SFormula({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Deriving the all-rounder…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Human interaction is one equation.
          <small>Z is the probability-weighted value of any human commitment.</small>
        </motion.h1>
        <motion.div className="pd-formula" variants={fadeUp}>
          Z = ∫₀ᵀ <b>Θ_sig</b> · <b>Θ_ch</b> · V(t) · <b>Φ</b> · <b>Ĉ</b> · E · F dt
        </motion.div>
        <motion.div className="pd-terms" variants={stagger}>
          {FORCES.map((x) => (
            <motion.div key={x.s} className="pd-term" variants={fadeUp}>
              <span className="s">{x.s}</span>
              <span className="f">{x.f}</span>
              <span className="k">{x.k}</span>
            </motion.div>
          ))}
          <motion.div className="pd-term" variants={fadeUp}>
            <span className="s">h(t)</span>
            <span className="f">and when it lands</span>
            <span className="k">½ in the final 30s</span>
          </motion.div>
        </motion.div>
        <motion.p className="pd-fine" variants={fadeUp}>
          Economics · Psychology · Information theory — three disciplines, one equation.
          Sources: DocSend · HBR 2011 (1.25M leads) · Cowan 2001 · Boomerang (40M emails) ·
          Schmidt &amp; Eisend · Roth 1988.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * ONE FORMULA, NOT SEVENTEEN — the credibility slide
 * ====================================================================== */
const KILLED = [
  { t: 'Ego depletion', d: 'd = 0.62 in the headlines → 0.04 under replication' },
  { t: 'Choice overload', d: 'the jam study: 30% → 3% buying → ≈0.02 meta-analytic' },
  { t: 'First-name personalization', d: '+20% in 2016 → null in 2024' },
  { t: 'Morning morality', d: 'g = 0.04 — indistinguishable from noise' },
];

export function SKillList({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Testing whether it is really one…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          We kept only what survives replication.
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          Candidate terms mined from ~150 sources across ten disciplines, then selected with the
          penalty set by how robustly each effect replicates. The famous ones did not make it.
        </motion.p>
        <motion.div
          className="pd-vgrid"
          style={{ gridTemplateColumns: '1fr', marginTop: 20 }}
          variants={stagger}
        >
          <motion.div className="pd-vcard" variants={fadeUp}>
            <h4>Killed — kept out of the equation</h4>
            <ul>
              {KILLED.map((k) => (
                <li key={k.t}>
                  <b>{k.t}</b> — {k.d}
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
        <motion.p className="pd-fine" variants={fadeUp}>
          Brown et al. 2024 (607-estimate meta-analysis) · Cowan 2001 · Boomerang 40M-email
          corpus · J. Finance 2017 RCT · selector sanity-checked on synthetic data: 9 of 9 real
          terms recovered, 5 of 5 noise terms zeroed.
        </motion.p>
        <motion.p className="pd-vsay" variants={fadeUp}>
          That is the difference between a <b>model</b> and a metaphor.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * THE DIALS — sales vs fundraising, and whether vs when
 * ====================================================================== */
const DIALS = [
  ['Who trusts you', 'referrals ×2–5', 'warm intro ×13 — 58% of VC deals come through networks, 10% cold'],
  ['How fast it dies', 'hours', 'days to weeks — deal heat ≈42 days'],
  ['What dominates the message', 'clarity of the value', 'the team — traction has ≈zero effect at early stage'],
  ['How the close spikes', 'the deadline', 'the goal — 2× likelier at 80% of the round than at 20%'],
];

export function SDials({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Turning the dials…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Sales and fundraising are not different arts.
          <small>They are the same equation with different numbers.</small>
        </motion.h1>
        <motion.div className="pd-dials" variants={fadeUp}>
          <div className="h">The dial</div>
          <div className="h">Sales</div>
          <div className="h">Fundraising</div>
          {DIALS.map(([d, s, f]) => (
            <Fragment key={d}>
              <div className="r">{d}</div>
              <div>{s}</div>
              <div>{f}</div>
            </Fragment>
          ))}
        </motion.div>
        <motion.p className="pd-vsay" variants={fadeUp}>
          And <b>when</b> it lands is its own equation — about half of all agreements are signed in
          the final 30 seconds. <b>One formula for whether, one for when.</b>
        </motion.p>
        <motion.p className="pd-fine" variants={fadeUp}>
          A model, not a law: the constants have to be refitted, and channels drift ≈15% a year.
          That refit loop is part of the system, not an accessory.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * THE HARNESS — the build, and the callback to the hammer
 * ====================================================================== */
const HARNESS = [
  { num: '01', title: 'Reach a human', desc: 'governed by Θ_ch — who trusts you', hero: true },
  { num: '02', title: 'Say it in one breath', desc: 'Ĉ · Φ — short, four things' },
  { num: '03', title: 'Move in the first hour', desc: 'V(t) — attention dies in minutes' },
  { num: '04', title: 'Check what comes back', desc: 'a human validates the evidence' },
  { num: '05', title: 'Sign and record', desc: 'Θ_sig — the signature is the costly signal' },
];

export function SHarness({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Building the handshake…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          A harness on one all-rounder backend.
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          Everything a company does with a human becomes an <b>endpoint</b>: hire, brief, check,
          pay, sign. We are building that set — and the formula decides which endpoint to use, in
          what order, and when.
        </motion.p>
        <motion.div className="pd-journey" variants={fadeUp}>
          <div className="pd-jline">
            <motion.i
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 2.2, ease: EASEX, delay: 0.8 }}
              style={{ transformOrigin: 'left' }}
            />
          </div>
          <div className="pd-jrow">
            {HARNESS.map((m, i) => (
              <motion.div
                key={m.title}
                className={cn('pd-jnode', m.hero && 'hero')}
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASEX, delay: 0.9 + i * 0.28 }}
              >
                <div className="pd-jdot" />
                <div className="pd-jnum">{m.num}</div>
                <div className="pd-jtitle">{m.title}</div>
                <div className="pd-jdesc">{m.desc}</div>
              </motion.div>
            ))}
          </div>
          <div className="pd-jpath">
            That is how a robot gets to attend a pitch event: <b>not by being liked</b> — by having
            endpoints, a formula, and a human who signs.
          </div>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * THE REGISTRY (D-063)
 *
 *   HAMMER · cover · THE LENS (input › process › output) · PROBLEM 1 ·
 *   PROBLEM 2 · THE NETWORK · the platform prepares · the gatekeepers ·
 *   the memory · education · FORMULA · KILL LIST · DIALS · HARNESS · spine
 *
 * The lens now arrives BEFORE the problems: it is the shape the deck thinks in,
 * so the problems can be stated against it. Problems + network are re-cut from
 * D-062; his 5-founders hook and the era slide stay out (see D-062).
 * ====================================================================== */
export const DECK_SLIDES_STORY: Array<(p: { dir: number }) => ReactElement> = [
  SHammer,
  DECK_SLIDES_VISION[0], // cover
  SEntityShape, // the lens — input › process › output
  SProblemSlow, // problem 1
  SProblemMachine, // problem 2
  SNetwork, // the directory, drawn
  DECK_SLIDES_VISION[5], // the platform prepares everything / the human decides
  DECK_SLIDES_VISION[8], // humans become the gatekeepers
  DECK_SLIDES_VISION[9], // the memory that makes AI accountable
  DECK_SLIDES_VISION[10], // education is the first market
  SFormula,
  SKillList,
  SDials,
  SHarness,
  ...DECK_SLIDES_VISION.slice(11), // the product spine → use of proceeds
];

/** Same assets as v2/v3 — every story visual is drawn inline, so nothing to preload. */
export const PRELOAD_STORY = PRELOAD_VISION;
